"use client";

import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const HASHES = ["a3f9c1", "7b2e44", "c81d05", "e4920a", "f15b3c"];

// État cumulatif du site à chaque commit.
const STEPS = [
    { msgKey: "Git_time_commit_1", paragraph: false, accent: false, button: false, image: false },
    { msgKey: "Git_time_commit_2", paragraph: true, accent: false, button: false, image: false },
    { msgKey: "Git_time_commit_3", paragraph: true, accent: true, button: false, image: false },
    { msgKey: "Git_time_commit_4", paragraph: true, accent: true, button: true, image: false },
    { msgKey: "Git_time_commit_5", paragraph: true, accent: true, button: true, image: true },
];

export default function GitTimeMachine() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [head, setHead] = useState(0);
    const [viewing, setViewing] = useState(0);

    const max = STEPS.length - 1;
    const step = STEPS[viewing];
    const isPast = viewing < head;

    function commit() {
        if (head >= max) return;
        const next = head + 1;
        setHead(next);
        setViewing(next);
    }

    function reset() {
        setHead(0);
        setViewing(0);
    }

    return (
        <section id="git-time-machine" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Git_time_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Git_time_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (teinte GitHub violet) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-violet-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-fuchsia-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:git-icon"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        mon-site
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Git_time_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7 grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                    {/* Aperçu du site à la version consultée */}
                    <div className="min-w-0">
                        <AnimatePresence initial={false}>
                            {isPast && (
                                <motion.div
                                    key="past"
                                    initial={
                                        reduce ? false : { opacity: 0, y: -6 }
                                    }
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={
                                        reduce
                                            ? { opacity: 0 }
                                            : { opacity: 0, y: -6 }
                                    }
                                    className="mb-3 flex items-center gap-2 rounded-lg bg-amber-500/10 ring-1 ring-amber-400/30 px-3 py-2 text-[13px] text-amber-200"
                                >
                                    <SolarIcon
                                        name="history-bold-duotone"
                                        className="w-4 h-4 shrink-0"
                                    />
                                    {t("Git_time_past_banner")}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Cadre navigateur */}
                        <div
                            className={`rounded-2xl overflow-hidden bg-white shadow-2xl shadow-black/40 transition-all duration-300 ${
                                isPast
                                    ? "ring-2 ring-amber-400/60"
                                    : "ring-1 ring-black/10"
                            }`}
                        >
                            {/* Barre d'URL */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
                                <div className="flex gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                </div>
                                <div className="flex-1 ml-1 inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 ring-1 ring-slate-200">
                                    <SolarIcon
                                        name="lock-keyhole-minimalistic-bold"
                                        className="w-3 h-3 text-slate-400"
                                    />
                                    <span className="text-[10px] font-medium text-slate-400">
                                        mon-site.fr
                                    </span>
                                </div>
                            </div>

                            {/* Page (transition entre versions) */}
                            <div className="min-h-[248px]">
                                <motion.div
                                    key={`${viewing}`}
                                    initial={
                                        reduce ? false : { opacity: 0, y: 8 }
                                    }
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: reduce ? 0 : 0.3,
                                        ease: "easeOut",
                                    }}
                                    className="p-5 sm:p-6"
                                >
                                    {/* Nav du site */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`w-6 h-6 rounded-lg ${
                                                    step.accent
                                                        ? "bg-violet-600"
                                                        : "bg-slate-900"
                                                }`}
                                            />
                                            <span
                                                className={`h-2.5 w-14 rounded-full ${
                                                    step.accent
                                                        ? "bg-violet-200"
                                                        : "bg-slate-200"
                                                }`}
                                            />
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="h-2 w-7 rounded-full bg-slate-200" />
                                            <span className="h-2 w-7 rounded-full bg-slate-200" />
                                            <span className="h-2 w-7 rounded-full bg-slate-200" />
                                        </div>
                                    </div>

                                    {/* Hero */}
                                    <div
                                        className={`mt-6 text-2xl font-extrabold tracking-tight ${
                                            step.accent
                                                ? "text-violet-600"
                                                : "text-slate-900"
                                        }`}
                                    >
                                        {t("Git_time_page_heading")}
                                    </div>

                                    {step.paragraph && (
                                        <div className="mt-3 space-y-2">
                                            <div className="h-2.5 rounded-full bg-slate-200 w-full" />
                                            <div className="h-2.5 rounded-full bg-slate-200 w-4/5" />
                                            <div className="h-2.5 rounded-full bg-slate-200 w-2/3" />
                                        </div>
                                    )}

                                    {step.button && (
                                        <div
                                            className={`mt-5 inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm ${
                                                step.accent
                                                    ? "bg-violet-600 shadow-violet-600/30"
                                                    : "bg-slate-900"
                                            }`}
                                        >
                                            {t("Git_time_page_button")}
                                        </div>
                                    )}

                                    {step.image && (
                                        <div
                                            className={`mt-5 h-28 rounded-xl flex items-center justify-center ring-1 ${
                                                step.accent
                                                    ? "bg-gradient-to-br from-violet-100 to-fuchsia-100 ring-violet-200"
                                                    : "bg-slate-100 ring-slate-200"
                                            }`}
                                        >
                                            <SolarIcon
                                                name="gallery-wide-bold-duotone"
                                                className={`w-8 h-8 ${
                                                    step.accent
                                                        ? "text-violet-400"
                                                        : "text-slate-300"
                                                }`}
                                            />
                                        </div>
                                    )}
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline des commits + contrôles */}
                    <div className="min-w-0 flex flex-col">
                        {/* En-tête du panneau */}
                        <div className="flex items-center gap-2 mb-4">
                            <SolarIcon
                                name="history-bold-duotone"
                                className="w-4 h-4 text-violet-300"
                            />
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-violet-300/90">
                                {t("Git_time_history_label")}
                            </span>
                            <span className="ml-auto font-mono text-[11px] text-gray-500">
                                {head + 1}/{STEPS.length}
                            </span>
                        </div>

                        {/* Liste des commits */}
                        <div className="flex flex-col">
                            <AnimatePresence initial={false}>
                                {STEPS.slice(0, head + 1).map((s, i) => {
                                    const active = i === viewing;
                                    const isHead = i === head;
                                    const last = i === head;
                                    return (
                                        <motion.button
                                            key={i}
                                            initial={
                                                reduce
                                                    ? false
                                                    : { opacity: 0, x: -12 }
                                            }
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={
                                                reduce
                                                    ? { opacity: 0 }
                                                    : { opacity: 0, x: -12 }
                                            }
                                            transition={{
                                                duration: reduce ? 0 : 0.3,
                                                ease: "easeOut",
                                            }}
                                            type="button"
                                            onClick={() => setViewing(i)}
                                            className={`group flex items-stretch gap-3 text-left rounded-lg -mx-2 px-2 py-1 transition-colors ${
                                                active
                                                    ? "bg-violet-500/10"
                                                    : "hover:bg-white/[0.03]"
                                            }`}
                                        >
                                            {/* Rail */}
                                            <div className="flex flex-col items-center w-4 pt-1.5">
                                                <span className="relative flex items-center justify-center">
                                                    {isHead && !reduce && (
                                                        <span className="absolute inline-flex h-4 w-4 rounded-full bg-violet-400/40 animate-ping" />
                                                    )}
                                                    <span
                                                        className={`relative w-3.5 h-3.5 rounded-full ring-2 transition-all ${
                                                            active
                                                                ? "bg-violet-400 ring-violet-300 scale-110"
                                                                : "bg-gray-600 ring-transparent group-hover:bg-gray-400"
                                                        }`}
                                                    />
                                                </span>
                                                {!last && (
                                                    <span className="w-0.5 flex-1 bg-gradient-to-b from-violet-400/40 to-white/10 min-h-[20px] mt-1" />
                                                )}
                                            </div>
                                            {/* Contenu */}
                                            <div className="pb-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-[11px] text-gray-500">
                                                        {HASHES[i]}
                                                    </span>
                                                    {isHead && (
                                                        <span className="rounded bg-violet-500/20 ring-1 ring-violet-400/40 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-300">
                                                            {t(
                                                                "Git_time_head_label"
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                <div
                                                    className={`text-sm ${
                                                        active
                                                            ? "text-white font-medium"
                                                            : "text-gray-400"
                                                    }`}
                                                >
                                                    {t(s.msgKey)}
                                                </div>
                                            </div>
                                        </motion.button>
                                    );
                                })}
                            </AnimatePresence>
                        </div>

                        {/* Contrôles */}
                        <div className="mt-2 pt-4 border-t border-white/10 flex flex-wrap items-center gap-3">
                            {head < max ? (
                                <button
                                    type="button"
                                    onClick={commit}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-violet-600/25 transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="code-square-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Git_time_commit_button")}
                                </button>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300">
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Git_time_done_label")}
                                </span>
                            )}

                            {isPast && (
                                <button
                                    type="button"
                                    onClick={() => setViewing(head)}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3.5 py-2 text-sm text-gray-200 transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="alt-arrow-right-bold"
                                        className="w-4 h-4"
                                    />
                                    {t("Git_time_present_button")}
                                </button>
                            )}

                            {head > 0 && (
                                <button
                                    type="button"
                                    onClick={reset}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1.5 text-xs text-gray-400 transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="restart-bold-duotone"
                                        className="w-3.5 h-3.5"
                                    />
                                    {t("Git_time_reset_button")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Git_time_caption")}
            </p>
        </section>
    );
}
